import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Package, Wand2, X, Repeat, ShoppingBag } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { RequestModal } from "../components/RequestModal";

const CITIES = ["Accra", "Tema", "Kumasi"];

function BoxCard({ box, onOpen }) {
  const { format } = useCurrency();
  const { main, sub } = format(box.price_ghs, box.price_gbp);
  const isSub = box.box_type === "subscription";
  return (
    <button onClick={() => onOpen(box)} data-testid={`box-${box.id}`}
      className="group text-left flex flex-col bg-white border border-[#0D530E]/8 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(13,83,14,0.12)] hover:border-[#0D530E]/15"
      style={{ transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease" }}>
      <div className="relative aspect-square overflow-hidden bg-[#E7E1B1]">
        <img src={box.image} alt={box.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
        <span className={`absolute top-2 left-2 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full ${isSub ? "bg-[#306D29] text-[#FBF5DD]" : "bg-white/90 text-[#0D530E]"}`}>
          {isSub ? "Monthly" : "One-time"}
        </span>
      </div>
      <div className="p-3 flex flex-col gap-1 flex-1">
        <h3 className="font-display text-lg leading-tight text-[#0D530E]">{box.name}</h3>
        <p className="text-[11px] text-[#306D29]/70 line-clamp-1">{box.items?.length || 0} items · {box.tagline}</p>
        <div className="mt-auto pt-1">
          <p className="font-display text-lg font-semibold text-[#0D530E] leading-none">{main}{isSub && <span className="text-xs font-normal">/mo</span>}</p>
          <p className="text-[11px] text-[#306D29]/70 mt-0.5">{sub}{isSub ? " / month" : ""}</p>
        </div>
      </div>
    </button>
  );
}

export default function Boxes() {
  const [boxes, setBoxes] = useState([]);
  const [seg, setSeg] = useState("subscription");
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", delivery_city: "Accra" });

  useEffect(() => { api.get("/boxes").then((r) => setBoxes(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user && user.email) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", customer_email: f.customer_email || user.email || "", customer_phone: f.customer_phone || user.phone || "", delivery_city: user.city || f.delivery_city }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const list = boxes.filter((b) => b.box_type === seg);

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const mode = selected.box_type === "subscription" ? "subscribe" : "once";
      const { data } = await api.post("/checkout/subscribe", { ...form, box_id: selected.id, mode, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const input = "w-full bg-[#FBF5DD] border border-[#0D530E]/15 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#306D29] focus:outline-none";
  const chip = (active) => `px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap border ${active ? "bg-[#306D29] text-[#FBF5DD] border-[#306D29]" : "bg-white border-[#0D530E]/12 text-[#0D530E] hover:border-[#306D29]"}`;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-center gap-2 mb-1 text-[#306D29]"><Package size={18} /><span className="text-xs tracking-[0.2em] uppercase font-semibold">Boxes</span></div>
      <h1 className="font-display text-5xl sm:text-6xl font-light tracking-tight text-[#0D530E] mb-3">Curated boxes</h1>
      <p className="text-[#306D29]/80 text-lg font-light max-w-2xl mb-8">Hand-picked products and quantities, packed into one box. Buy a box once, or subscribe and receive it every month.</p>

      <div className="flex gap-2 mb-8">
        <button data-testid="seg-subscription" onClick={() => setSeg("subscription")} className={chip(seg === "subscription")} style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}><Repeat size={14} className="inline mr-1.5 -mt-0.5" />Subscription Boxes</button>
        <button data-testid="seg-one_time" onClick={() => setSeg("one_time")} className={chip(seg === "one_time")} style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}><ShoppingBag size={14} className="inline mr-1.5 -mt-0.5" />One-time Boxes</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5" data-testid="box-grid">
        {list.map((b) => <BoxCard key={b.id} box={b} onOpen={setSelected} />)}
        <button onClick={() => setCustom(true)} data-testid="box-custom"
          className="flex flex-col items-start justify-center text-left rounded-xl border border-dashed border-[#306D29]/40 bg-[#E7E1B1]/30 p-5 hover:bg-[#E7E1B1]/50 min-h-[220px]" style={{ transition: "background-color 0.2s ease" }}>
          <div className="w-11 h-11 rounded-full bg-[#306D29] text-[#FBF5DD] flex items-center justify-center mb-3"><Wand2 size={20} /></div>
          <h3 className="font-display text-xl text-[#0D530E]">Custom Box</h3>
          <p className="text-sm text-[#306D29]/80 mt-1">Tell us what to include & your budget. We'll curate it.</p>
          <span className="mt-3 text-sm font-medium text-[#306D29] underline">Create my box →</span>
        </button>
      </div>

      {/* Box detail + checkout */}
      {selected && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !loading && setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="box-detail-modal">
            <div className="relative aspect-[16/9] overflow-hidden bg-[#E7E1B1] rounded-t-3xl">
              <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
              <button onClick={() => setSelected(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-[#0D530E]"><X size={18} /></button>
              <span className="absolute top-3 left-3 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#306D29] text-[#FBF5DD]">{selected.box_type === "subscription" ? "Monthly subscription" : "One-time box"}</span>
            </div>
            {selected.gallery?.length > 1 && (
              <div className="flex gap-2 px-7 pt-4 overflow-x-auto" data-testid="box-gallery">
                {selected.gallery.map((g, i) => (
                  <button key={i} type="button" onClick={() => setSelected({ ...selected, image: g })}
                    className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 ${selected.image === g ? "border-[#306D29]" : "border-transparent"}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="p-7">
              <h3 className="font-display text-3xl text-[#0D530E]">{selected.name}</h3>
              <p className="text-[#306D29] font-medium mb-4">{selected.tagline}</p>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#306D29] mb-2">What's inside</p>
              <ul className="space-y-1.5 mb-5">
                {(selected.items || []).map((it, i) => (
                  <li key={i} className="flex items-center justify-between text-sm text-[#0D530E] border-b border-[#0D530E]/5 pb-1.5">
                    <span className="flex items-center gap-2"><Check size={14} className="text-[#306D29]" /> {it.name}</span>
                    <span className="font-medium text-[#306D29]">×{it.qty}</span>
                  </li>
                ))}
              </ul>
              <p className="mb-4"><span className="font-display text-2xl font-semibold text-[#0D530E]">GH₵ {selected.price_ghs.toLocaleString()}</span>
                <span className="text-[#306D29]/70 text-sm"> · £{selected.price_gbp.toFixed(2)}{selected.box_type === "subscription" ? " / month" : ""}</span></p>
              <form onSubmit={checkout} className="space-y-3">
                <input required className={input} placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} data-testid="box-name" />
                <input required type="email" className={input} placeholder="Email" value={form.customer_email} onChange={set("customer_email")} data-testid="box-email" />
                <input required className={input} placeholder="Phone" value={form.customer_phone} onChange={set("customer_phone")} data-testid="box-phone" />
                <select className={input} value={form.delivery_city} onChange={set("delivery_city")}>{CITIES.map((c) => <option key={c}>{c}</option>)}</select>
                <button type="submit" disabled={loading} data-testid="box-pay" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-[#FBF5DD] font-medium py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
                  {loading && <Loader2 size={18} className="animate-spin" />}{selected.box_type === "subscription" ? "Subscribe & pay" : "Buy this box"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <RequestModal open={custom} onClose={() => setCustom(false)} kind="custom_box" />
    </div>
  );
}
